import { useState } from 'react';
import type { platform } from '@platform/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  MailIcon,
  Page,
  PageHeader,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useToast,
  type BadgeTone,
} from '@platform/design-system';
import { describeError, longDate, useCanOpen } from '@platform/app-kit';
import {
  useAnnouncements,
  useCells,
  usePublishAnnouncement,
  useTenants,
  useWithdrawAnnouncement,
} from '../api/queries.ts';
import { ANNOUNCEMENT_PUBLISH_ROUTE } from '../shell/routes.ts';

type Audience = 'tenant' | 'person' | 'cell';

const SEVERITY: Record<platform.NoticeSeverity, { label: string; tone: BadgeTone }> = {
  info: { label: 'Information', tone: 'info' },
  important: { label: 'Important', tone: 'warning' },
  security: { label: 'Security: stays until read', tone: 'error' },
};

const AUDIENCE_OPTIONS: { value: Audience; label: string }[] = [
  { value: 'tenant', label: 'Everyone in one tenant' },
  { value: 'person', label: 'One person in a tenant' },
  { value: 'cell', label: 'Everyone in every active tenant of a cell' },
];

function describeTarget(target: platform.AnnouncementTarget): string {
  if (target.cellId) return `cell ${target.cellId}`;
  return target.sub ? `${target.tenantId}, one person` : `${target.tenantId}`;
}

function audienceSentence(audience: Audience, cellId: string, tenantId: string): string {
  if (audience === 'cell') return `everyone in every active tenant of ${cellId}`;
  if (audience === 'person') return `one person in ${tenantId}`;
  return `everyone in ${tenantId}`;
}

/**
 * `/notices` — what staff say to people inside the apps (ADR in-app-notices-from-staff). A notice
 * is shown above the page until the person acknowledges it; the breach rule counts it as the
 * in-app half of the email-plus-banner pairing. Plain text only. Every publish is audited on each
 * tenant's log with the staff member who sent it.
 */
export function NoticesPage() {
  const toast = useToast();
  const tenants = useTenants();
  const cells = useCells();
  const list = useAnnouncements();
  const publish = usePublishAnnouncement();
  const withdraw = useWithdrawAnnouncement();
  const canPublish = useCanOpen(ANNOUNCEMENT_PUBLISH_ROUTE);

  const [audience, setAudience] = useState<Audience>('tenant');
  const [tenantId, setTenantId] = useState('');
  const [sub, setSub] = useState('');
  const [cellId, setCellId] = useState('');
  const [severity, setSeverity] = useState<platform.NoticeSeverity>('important');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [expires, setExpires] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [withdrawing, setWithdrawing] = useState<platform.Announcement | null>(null);

  const target: platform.AnnouncementTarget =
    audience === 'cell'
      ? { cellId }
      : { tenantId, ...(audience === 'person' && sub.trim() ? { sub: sub.trim() } : {}) };
  const linkOk = (linkLabel.trim() === '') === (linkUrl.trim() === '');
  const linkError =
    linkUrl.trim() !== '' && !linkUrl.trim().startsWith('https://')
      ? 'Start with https://'
      : undefined;
  const ready =
    title.trim() !== '' &&
    body.trim() !== '' &&
    linkOk &&
    linkError === undefined &&
    (audience === 'cell' ? cellId !== '' : tenantId !== '') &&
    (audience !== 'person' || sub.trim() !== '');

  const send = () =>
    publish.mutate(
      {
        target,
        severity,
        title: title.trim(),
        body: body.trim(),
        ...(linkUrl.trim() ? { link: { label: linkLabel.trim(), url: linkUrl.trim() } } : {}),
        ...(expires ? { expiresAt: new Date(expires).toISOString() } : {}),
      },
      {
        onSuccess: (made) => {
          setConfirming(false);
          setTitle('');
          setBody('');
          setLinkLabel('');
          setLinkUrl('');
          setExpires('');
          toast.success(
            `Sent to ${made.tenantCount} ${made.tenantCount === 1 ? 'tenant' : 'tenants'}`,
          );
        },
        onError: (e) => {
          setConfirming(false);
          toast.error(describeError(e, 'We couldn’t send that. Try again.'));
        },
      },
    );

  const sent = list.data ?? [];

  return (
    <Page width="narrow">
      <PageHeader
        title="Notices"
        meta="Show a message inside the apps. It stays above the page until the person acknowledges it."
      />
      {canPublish && (
        <Card title="New notice">
          <Stack
            spacing={2}
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (ready) setConfirming(true);
            }}
          >
            <Select
              label="Who sees it"
              value={audience}
              onChange={(value) => setAudience(value)}
              options={AUDIENCE_OPTIONS}
            />
            {audience === 'cell' ? (
              <Select
                label="Cell"
                value={cellId}
                placeholder="Choose a cell"
                onChange={(value) => setCellId(String(value))}
                options={(cells.data ?? []).map((c) => ({
                  value: c.cellId,
                  label: `${c.cellId} (${c.product}, ${c.tenantCount} tenants)`,
                }))}
              />
            ) : (
              <Select
                label="Tenant"
                value={tenantId}
                placeholder="Choose a tenant"
                onChange={(value) => setTenantId(String(value))}
                options={(tenants.data ?? []).map((t) => ({
                  value: t.tenantId,
                  label: `${t.name} (${t.tenantId})`,
                }))}
              />
            )}
            {audience === 'person' && (
              <TextField
                label="Person’s sign-in id"
                hint="Their sub, from the tenant’s members."
                value={sub}
                onChange={(e) => setSub(e.target.value)}
              />
            )}
            <Select
              label="Kind"
              value={severity}
              onChange={(value) => setSeverity(value)}
              options={(Object.keys(SEVERITY) as platform.NoticeSeverity[]).map((k) => ({
                value: k,
                label: SEVERITY[k].label,
              }))}
            />
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 120 } }}
            />
            <TextField
              label="Message"
              hint="Plain text, up to 2,000 characters. No health information."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={4}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Link text (optional)"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
              />
              <TextField
                label="Link address (optional)"
                hint="https only"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                error={linkError}
              />
            </Stack>
            <TextField
              label="Stop showing it on (optional)"
              type="datetime-local"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!ready}
              sx={{ alignSelf: 'flex-start' }}
            >
              Review and send
            </Button>
          </Stack>
        </Card>
      )}

      <Card title="Sent">
        {list.isLoading ? (
          <Skeleton height={80} />
        ) : list.isError ? (
          <Alert severity="warning">
            {describeError(list.error, 'We couldn’t load the notices.')}
          </Alert>
        ) : sent.length === 0 ? (
          <EmptyState
            plain
            icon={<MailIcon />}
            title="Nothing sent yet"
            description="Notices you send appear here, and you can take one back."
            action={<span />}
          />
        ) : (
          <Stack spacing={2} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {sent.map((a) => (
              <Stack key={a.announcementId} component="li" spacing={0.5}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge tone={SEVERITY[a.severity].tone} label={SEVERITY[a.severity].label} />
                  {a.withdrawnAt && <Badge label="Withdrawn" />}
                  <Typography variant="subtitle2" component="p">
                    {a.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  To {describeTarget(a.target)} · {a.tenantCount}{' '}
                  {a.tenantCount === 1 ? 'tenant' : 'tenants'} · {longDate(a.publishedAt)}
                </Typography>
                {!a.withdrawnAt && (
                  <Button
                    size="small"
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() => setWithdrawing(a)}
                  >
                    Withdraw
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      <ConfirmDialog
        open={confirming}
        title="Send this notice?"
        message={`It will be shown to ${audienceSentence(audience, cellId, tenantId)}, above the page, until they acknowledge it. Each tenant’s audit log records that you sent it.`}
        confirmLabel="Send notice"
        busy={publish.isPending}
        onConfirm={send}
        onClose={() => setConfirming(false)}
      />
      <ConfirmDialog
        open={withdrawing !== null}
        title="Take this notice back?"
        message="People who have not acknowledged it stop seeing it."
        confirmLabel="Withdraw"
        busy={withdraw.isPending}
        onConfirm={() =>
          withdrawing &&
          withdraw.mutate(withdrawing.announcementId, {
            onSuccess: () => {
              setWithdrawing(null);
              toast.success('Notice withdrawn');
            },
            onError: (e) => toast.error(describeError(e, 'We couldn’t withdraw that. Try again.')),
          })
        }
        onClose={() => setWithdrawing(null)}
      />
    </Page>
  );
}
